// src/autoView.js — generic, form-AGNOSTIC default view used when no per-form
// view is registered. Renders something sensible for ANY submission.

export function createAutoView(html, C) {
  const { Hero, Section, KPI, FieldList, Gallery, LabTable, RepeatedTable, TrendChart, Callout, StatGrid } = C;

  return function autoView(d) {
    const meta = d.meta || {};
    const wo = d.workOrder; // null when the submission has no linked work order
    const subtitle = meta.submitterEmail || meta.submitterName || '';

    // Numeric scalar fields → a prominent headline (big KPIs) + a denser supporting strip below it,
    // giving the page three visual tiers (headline → supporting → detail) instead of one flat KPI row.
    const numericFields = d.fields().filter((f) => f && isPlainScalar(f) && isNumeric(f.value));
    const headline = numericFields.slice(0, 3);
    const supporting = numericFields.slice(3, 7);

    const images = d.images();
    const repeatedFields = d.fields().filter((f) => f && isRepeated(f));
    const lab = d.labResults();

    const overdue = isOverdue(wo);

    return html`<div class="aq-doc">
      <${Hero} title=${d.formName} subtitle=${subtitle} meta=${meta} />

      ${overdue
        ? html`<${Callout} tone="err" title="Work order overdue">
            ${`Due ${formatDay(wo.dueDate)}${wo.assignedToUserName ? ` · ${wo.assignedToUserName}` : ''}`}
          <//>`
        : null}

      ${headline.length
        ? html`<div class="aq-row">
            ${headline.map(
              (f) => html`<${KPI} key=${f.fieldKey} label=${d.label(f.fieldKey)} value=${d.num(f.fieldKey)} />`
            )}
          </div>`
        : null}

      ${supporting.length
        ? html`<${StatGrid} items=${supporting.map((f) => ({ label: d.label(f.fieldKey), value: d.num(f.fieldKey) }))} />`
        : null}

      ${wo
        ? html`<${Section} title="Work order">
            <${StatGrid} items=${workOrderTiles(wo)} />
          <//>`
        : null}

      <${Section} title="Details">
        <${FieldList} fields=${d.fields()} d=${d} />
      <//>

      ${images.length || d.attachmentsAll().length
        ? html`<${Section} title="Attachments">
            <${Gallery} items=${d.attachmentsAll()} />
          <//>`
        : null}

      ${repeatedFields.map((f) => {
        const entries = d.repeated(f.fieldKey);
        if (!entries.length) return null;
        const numericKey = firstNumericChildKey(entries);
        const points = numericKey ? d.series(f.fieldKey, numericKey) : [];
        return html`<${Section} title=${d.label(f.fieldKey)} key=${f.fieldKey}>
          <${RepeatedTable} entries=${entries} d=${d} />
          ${points.length > 1
            ? html`<${TrendChart} title=${`${d.label(f.fieldKey)} · ${labelForKey(entries, numericKey)}`} points=${points} />`
            : null}
        <//>`;
      })}

      ${lab.length
        ? html`<${Section} title="Lab results"><${LabTable} rows=${lab} /><//>`
        : null}
    </div>`;
  };
}

// ---- helpers --------------------------------------------------------------
const ATTACH_REP = [
  'image', 'drawing', 'voice', 'video', 'document', 'file', 'signature',
  'repeatedform', 'repeated', 'attachment'
];

function isPlainScalar(f) {
  const t = String(f.fieldType || '').toLowerCase();
  if (ATTACH_REP.includes(t)) return false;
  const v = f.value;
  if (Array.isArray(v) && v.length && typeof v[0] === 'object') return false;
  return true;
}

function isNumeric(v) {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean') return false;
  if (Array.isArray(v)) return false;
  return Number.isFinite(Number(v));
}

function isRepeated(f) {
  const t = String(f.fieldType || '').toLowerCase();
  if (t === 'repeatedform' || t === 'repeated') return true;
  const v = f.value;
  return Array.isArray(v) && v.length > 0 && v[0] && typeof v[0] === 'object' && Array.isArray(v[0].fields);
}

function firstNumericChildKey(entries) {
  for (const e of entries) {
    for (const f of e.fields || []) {
      if (f && isPlainScalar(f) && isNumeric(f.value)) return f.fieldKey;
    }
  }
  return null;
}

function labelForKey(entries, key) {
  for (const e of entries) {
    if (e.h) return e.h.label(key);
  }
  return key;
}

// Map a submission/work-order STATUS to a semantic tone. Only known status vocabulary is toned — the kit
// never tones measured numbers (it has no business thresholds). Unknown → '' (neutral). Mirrors the kit's
// Badge/Callout vocabulary plus the SubmissionStatus + WorkOrderStatus enums (case/separator-insensitive).
function statusTone(s) {
  const t = String(s || '').toLowerCase().replace(/[\s_-]+/g, '');
  if (['ok', 'success', 'approved', 'complete', 'completed', 'closed', 'done', 'passed', 'pass'].includes(t)) return 'ok';
  if (['inprogress', 'underreview', 'review', 'pending', 'draft', 'open', 'scheduled', 'warn', 'warning'].includes(t)) return 'warn';
  if (['rejected', 'failed', 'fail', 'cancelled', 'canceled', 'overdue', 'error', 'err'].includes(t)) return 'err';
  return '';
}

// Work-order priority → tone. 'low'/unknown stays neutral (green would wrongly imply "good").
function priorityTone(p) {
  const t = String(p || '').toLowerCase();
  if (['high', 'urgent', 'critical'].includes(t)) return 'err';
  if (['medium', 'normal', 'moderate'].includes(t)) return 'warn';
  return '';
}

// A work order is overdue when its due date is in the past and it has not been completed. A calendar fact,
// not a business threshold, so it is safe to surface.
function isOverdue(wo) {
  if (!wo || !wo.dueDate || wo.completedAt) return false;
  const due = new Date(wo.dueDate).getTime();
  return Number.isFinite(due) && due < Date.now();
}

// Build the Work-order section's stat tiles — each guarded so a partial work order never shows a blank tile.
function workOrderTiles(wo) {
  const tiles = [];
  if (wo.workOrderNumber) tiles.push({ label: 'Work order', value: `#${wo.workOrderNumber}` });
  if (wo.status) tiles.push({ label: 'Status', value: humanize(wo.status), tone: statusTone(wo.status) });
  if (wo.priority) tiles.push({ label: 'Priority', value: humanize(wo.priority), tone: priorityTone(wo.priority) });
  if (wo.assignedToUserName) tiles.push({ label: 'Assigned to', value: wo.assignedToUserName });
  if (wo.dueDate) tiles.push({ label: 'Due', value: formatDay(wo.dueDate), tone: isOverdue(wo) ? 'err' : '' });
  if (wo.serviceDate) tiles.push({ label: 'Service date', value: formatDay(wo.serviceDate) });
  if (wo.customerName) tiles.push({ label: 'Customer', value: wo.customerName });
  return tiles;
}

// "InProgress"/"in_progress" → "In Progress"; "high" → "High". Display-only.
function humanize(s) {
  const str = String(s == null ? '' : s);
  if (!str) return str;
  return str.replace(/[_-]+/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
}

function formatDay(iso) {
  try {
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return String(iso);
    return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return String(iso);
  }
}

export default createAutoView;
